import React, { useEffect, useRef, useState } from 'react';
import { Type, Trash2 } from 'lucide-react';

// Instagram-style photo caption editor.
// Renders the photo at its true aspect ratio with the caption overlaid
// on top. On desktop the caption is draggable (WYSIWYG). On any device,
// sliders provide a touch-friendly fallback for X/Y positioning and font
// size, plus color / alignment / weight / italic / shadow controls.
//
// Uploaded files come back as relative /api/files/... paths; the wedding
// site resolves those against REACT_APP_BACKEND_URL. We mirror the same
// logic here so the editor preview matches what guests will see.
const resolveMedia = (u) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
};

// Editor-side default matches the backend PhotoCaption model defaults.
const CAPTION_DEFAULTS = {
  enabled: false,
  text: '',
  x: 50,
  y: 85,
  fontSize: 28,
  color: '#FFFFFF',
  align: 'center',
  weight: 'bold',
  italic: false,
  shadow: true,
};

const COLOR_SWATCHES = ['#FFFFFF', '#000000', '#FBF7F2', '#7B2C3B', '#B8896C', '#4A7A4A', '#EFC9B0'];

export default function CaptionEditor({ testId, photoUrl, aspect = 'portrait', caption, onChange }) {
  // Local state lets us feel snappy while dragging; we push to the parent
  // (and hence to the backend) via `commit()`.
  const value = { ...CAPTION_DEFAULTS, ...(caption || {}) };
  const [local, setLocal] = useState(value);
  const rootRef = useRef(null);
  const draggingRef = useRef(false);

  // Sync local state when the parent value changes (e.g. after a save).
  useEffect(() => {
    setLocal({ ...CAPTION_DEFAULTS, ...(caption || {}) });
  }, [caption]);

  const patch = (p) => setLocal((prev) => ({ ...prev, ...p }));
  // Persist to parent — parent handles debouncing / API call.
  const commit = (p) => {
    const next = { ...local, ...p };
    setLocal(next);
    onChange && onChange(next);
  };

  // Drag-to-position: convert pointer coordinates into % of the photo box.
  const onPointerDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    if (rootRef.current) rootRef.current.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    patch({ x, y });
  };
  const onPointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (rootRef.current) rootRef.current.releasePointerCapture?.(e.pointerId);
    commit({}); // persist final position
  };

  const aspectClass = aspect === 'wide' ? 'aspect-[16/9]' : 'aspect-[3/4]';
  const src = resolveMedia(photoUrl);

  const align = local.align || 'center';
  const translate = align === 'left'
    ? 'translate(0, -50%)'
    : align === 'right'
      ? 'translate(-100%, -50%)'
      : 'translate(-50%, -50%)';
  const captionStyle = {
    position: 'absolute',
    left: `${local.x}%`,
    top: `${local.y}%`,
    transform: translate,
    color: local.color,
    fontSize: `${(local.fontSize / 640) * 100}cqw`,
    fontWeight: local.weight === 'normal' ? 400 : 700,
    fontStyle: local.italic ? 'italic' : 'normal',
    textAlign: align,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.15,
    maxWidth: '90%',
    textShadow: local.shadow ? '0 2px 12px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.45)' : 'none',
    cursor: local.enabled ? 'grab' : 'default',
    userSelect: 'none',
  };

  return (
    <div data-testid={testId} className="mt-3 rounded-2xl border border-[#EFE3D8] bg-[#FBF7F2] overflow-hidden">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm text-[#3A3A3A]">
          <Type className="w-4 h-4 text-[#7B2C3B]" />
          <input
            data-testid={`${testId}-enabled`}
            type="checkbox"
            checked={!!local.enabled}
            onChange={(e) => commit({ enabled: e.target.checked })}
            className="accent-[#7B2C3B]"
          />
          Add caption on this photo
        </label>
        {local.enabled && local.text && (
          <button
            data-testid={`${testId}-clear`}
            onClick={() => commit({ text: '' })}
            className="text-xs inline-flex items-center gap-1 text-[#7B2C3B] hover:underline"
          >
            <Trash2 className="w-3 h-3" /> Clear text
          </button>
        )}
      </div>

      {local.enabled && (
        <>
          {/* WYSIWYG preview */}
          <div
            ref={rootRef}
            data-testid={`${testId}-canvas`}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className={`relative w-full ${aspectClass} bg-black/10 select-none`}
            style={{ touchAction: 'none', containerType: 'inline-size' }}
          >
            {src && (
              <img
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                draggable={false}
              />
            )}
            {local.text && (
              <div
                data-testid={`${testId}-draggable`}
                onPointerDown={onPointerDown}
                style={captionStyle}
              >
                {local.text}
              </div>
            )}
            {!local.text && (
              <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm pointer-events-none">
                Type your caption below — then drag it anywhere on the photo.
              </div>
            )}
          </div>

          {/* Text + controls */}
          <div className="p-4 space-y-3">
            <textarea
              data-testid={`${testId}-text`}
              value={local.text || ''}
              onChange={(e) => patch({ text: e.target.value })}
              onBlur={() => commit({})}
              placeholder="e.g. Save the date · Sept 27, 2026"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B] resize-none"
            />

            {/* Color */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider text-[#6A6A6A]">Color</span>
              {COLOR_SWATCHES.map((c) => (
                <button
                  key={c}
                  data-testid={`${testId}-color-${c.replace('#', '')}`}
                  onClick={() => commit({ color: c })}
                  title={c}
                  className={`w-6 h-6 rounded-full border-2 ${local.color?.toLowerCase() === c.toLowerCase() ? 'border-[#7B2C3B]' : 'border-white'} shadow`}
                  style={{ background: c }}
                />
              ))}
              <input
                data-testid={`${testId}-color-picker`}
                type="color"
                value={/^#[0-9A-Fa-f]{6}$/.test(local.color || '') ? local.color : '#FFFFFF'}
                onChange={(e) => patch({ color: e.target.value })}
                onBlur={() => commit({})}
                className="w-8 h-8 rounded border border-[#EFE3D8] bg-white"
              />
            </div>

            {/* Style row */}
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-[11px] uppercase tracking-wider text-[#6A6A6A]">Style</span>
              {['left', 'center', 'right'].map((a) => (
                <button
                  key={a}
                  data-testid={`${testId}-align-${a}`}
                  onClick={() => commit({ align: a })}
                  className={`px-2 py-1 rounded-full border capitalize ${local.align === a ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}
                >{a}</button>
              ))}
              <span className="w-px h-4 bg-[#EFE3D8] mx-1" />
              <button
                data-testid={`${testId}-bold`}
                onClick={() => commit({ weight: local.weight === 'bold' ? 'normal' : 'bold' })}
                className={`px-2 py-1 rounded-full border font-bold ${local.weight === 'bold' ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}
              >B</button>
              <button
                data-testid={`${testId}-italic`}
                onClick={() => commit({ italic: !local.italic })}
                className={`px-2 py-1 rounded-full border italic ${local.italic ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}
              >I</button>
              <button
                data-testid={`${testId}-shadow`}
                onClick={() => commit({ shadow: !local.shadow })}
                className={`px-2 py-1 rounded-full border ${local.shadow ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}
                title="Toggle drop shadow — helps text stay readable on busy photos"
              >Shadow</button>
            </div>

            {/* Position + size sliders (touch-friendly fallback) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] text-[#6A6A6A]">
              <label className="flex flex-col gap-1">
                Font size · {local.fontSize}px
                <input
                  data-testid={`${testId}-fontsize`}
                  type="range" min={12} max={96} value={local.fontSize}
                  onChange={(e) => patch({ fontSize: Number(e.target.value) })}
                  onMouseUp={() => commit({})} onTouchEnd={() => commit({})}
                  className="w-full accent-[#7B2C3B]"
                />
              </label>
              <label className="flex flex-col gap-1">
                Horizontal · {Math.round(local.x)}%
                <input
                  data-testid={`${testId}-x`}
                  type="range" min={0} max={100} value={local.x}
                  onChange={(e) => patch({ x: Number(e.target.value) })}
                  onMouseUp={() => commit({})} onTouchEnd={() => commit({})}
                  className="w-full accent-[#7B2C3B]"
                />
              </label>
              <label className="flex flex-col gap-1">
                Vertical · {Math.round(local.y)}%
                <input
                  data-testid={`${testId}-y`}
                  type="range" min={0} max={100} value={local.y}
                  onChange={(e) => patch({ y: Number(e.target.value) })}
                  onMouseUp={() => commit({})} onTouchEnd={() => commit({})}
                  className="w-full accent-[#7B2C3B]"
                />
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
